import { createTheme, alpha } from '@mui/material/styles';

/**
 * Design system Ergofy (verde água) — alinhado ao projeto AEP.
 * Suporta tema claro e escuro via createAppTheme(mode).
 *
 * IMPORTANTE: as cores do SEMÁFORO COPSOQ (RISK_COLORS) são tokens
 * SEMÂNTICOS e NÃO mudam com a marca nem com o modo claro/escuro —
 * são significados clínicos validados (verde/amarelo/vermelho).
 */

// Cores de marca Ergofy
const BRAND = {
  primary: { main: '#4ACFAC', light: '#71DBBD', dark: '#33A888', contrastText: '#FFFFFF' },
  secondary: { main: '#2D3894', light: '#5C66B3', dark: '#1F2868', contrastText: '#FFFFFF' },
};

// Semáforo de risco psicossocial (NÃO ALTERAR — significado clínico)
export const RISK_COLORS = { green: '#16A34A', yellow: '#F59E0B', red: '#DC2626' };

const lightPalette = {
  mode: 'light',
  ...BRAND,
  success: { main: '#4ACFAC', light: '#71DBBD', dark: '#33A888' },
  warning: { main: '#f59e0b', light: '#fcd34d', dark: '#b45309' },
  error: { main: '#ef4444', light: '#fca5a5', dark: '#b91c1c' },
  info: { main: '#6366f1', light: '#93c5fd', dark: '#4338ca' },
  background: {
    default: '#f4f6fb',
    paper: '#ffffff',
    glass: alpha('#ffffff', 0.8),
    overlay: alpha('#000000', 0.4),
  },
  text: { primary: '#0f172a', secondary: '#475569', disabled: '#94a3b8' },
  divider: alpha('#0f172a', 0.08),
  risk: RISK_COLORS,
};

const darkPalette = {
  mode: 'dark',
  ...BRAND,
  success: { main: '#4ACFAC', light: '#71DBBD', dark: '#33A888' },
  warning: { main: '#fcd34d', light: '#fde68a', dark: '#f59e0b' },
  error: { main: '#fca5a5', light: '#fecaca', dark: '#ef4444' },
  info: { main: '#a5b4fc', light: '#c7d2fe', dark: '#6366f1' },
  background: {
    default: '#1b1a18',
    paper: '#211f1d',
    glass: alpha('#ffffff', 0.06),
    overlay: alpha('#000000', 0.55),
  },
  text: { primary: '#e6e8eb', secondary: '#9aa3ad', disabled: '#6b7480' },
  divider: alpha('#ffffff', 0.06),
  risk: RISK_COLORS,
};

const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
  h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.02em' },
  h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.015em' },
  h4: { fontSize: '1.1rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.01em' },
  h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.01em' },
  h6: { fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.4 },
  body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6 },
  body2: { fontSize: '0.8rem', fontWeight: 400, lineHeight: 1.5 },
  button: { fontWeight: 500, textTransform: 'none', fontSize: '0.8rem' },
  caption: { fontSize: '0.72rem', fontWeight: 400, lineHeight: 1.4 },
};

const buildComponents = (mode) => {
  const isDark = mode === 'dark';
  const cardBg = isDark
    ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)'
    : alpha('#ffffff', 0.8);
  const cardBorder = isDark ? alpha('#ffffff', 0.06) : alpha('#ffffff', 0.3);
  const cardShadow = isDark
    ? '0 8px 24px rgba(0,0,0,0.35)'
    : '0 8px 32px rgba(15,23,42,0.08)';
  const cardShadowHover = isDark
    ? '0 20px 50px -12px rgba(0,0,0,0.55)'
    : '0 12px 40px rgba(15,23,42,0.15)';
  const surface = isDark ? alpha('#ffffff', 0.06) : alpha('#ffffff', 0.85);
  const appbarBg = isDark ? alpha('#1b1a18', 0.85) : alpha('#ffffff', 0.9);
  const strokeColor = isDark ? alpha('#ffffff', 0.06) : alpha('#000000', 0.06);
  const textColor = isDark ? '#e6e8eb' : '#0f172a';

  return {
    MuiCard: {
      styleOverrides: {
        root: {
          background: cardBg,
          backdropFilter: 'blur(24px) saturate(150%)',
          WebkitBackdropFilter: 'blur(24px) saturate(150%)',
          border: `1px solid ${cardBorder}`,
          boxShadow: cardShadow,
          borderRadius: 14,
          transition: 'transform 0.18s ease, box-shadow 0.18s ease',
          '&:hover': { boxShadow: cardShadowHover, transform: 'translateY(-2px)' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: appbarBg,
          backdropFilter: 'blur(20px) saturate(150%)',
          WebkitBackdropFilter: 'blur(20px) saturate(150%)',
          borderBottom: `1px solid ${strokeColor}`,
          boxShadow: 'none',
          color: textColor,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 18px',
          fontSize: '0.8rem',
          transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s, opacity 0.15s',
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'scale(0.98)' },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 6px 18px rgba(74,207,172,0.30)' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #4ACFAC, #33A888)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: surface,
            transition: 'all 0.2s ease-in-out',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha('#4ACFAC', 0.4),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4ACFAC',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12, backgroundImage: 'none' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600, fontSize: '0.72rem' },
      },
    },
    MuiTableContainer: {
      styleOverrides: { root: { borderRadius: 12, overflow: 'hidden' } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: isDark ? alpha('#ffffff', 0.03) : alpha('#f4f6fb', 0.7),
            fontWeight: 600,
            fontSize: '0.75rem',
            color: isDark ? '#9aa3ad' : '#475569',
            borderBottom: `1px solid ${strokeColor}`,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: isDark ? alpha('#1b1a18', 0.92) : alpha('#ffffff', 0.85),
          backdropFilter: 'blur(30px) saturate(150%)',
          WebkitBackdropFilter: 'blur(30px) saturate(150%)',
          borderRight: `1px solid ${strokeColor}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: isDark ? '#211f1d' : alpha('#ffffff', 0.97),
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 14,
        },
      },
    },
  };
};

export const createAppTheme = (mode = 'light') =>
  createTheme({
    palette: mode === 'dark' ? darkPalette : lightPalette,
    typography,
    shape: { borderRadius: 12 },
    spacing: 8,
    components: buildComponents(mode),
  });

// Tema padrão (claro) para compatibilidade com imports existentes
export const theme = createAppTheme('light');
export default theme;
